import { useRef } from "react";
import { COLUMN_RESIZE_MIN_WIDTH } from "@/hooks/useColumnWidths";

interface ColumnResizeHandleProps {
  columnKey: string;
  currentWidth: number | undefined;
  defaultWidth?: number;
  minWidth?: number;
  /**
   * Notifica el ancho final al terminar el drag. El padre es responsable de
   * persistir (useColumnWidths.setWidth escribe en localStorage y state).
   * El handle no llama a este callback en cada move: durante el drag aplica
   * el width directamente al DOM para evitar que el handle se mueva junto
   * con la columna y rompa la medición del delta.
   */
  onCommit: (columnKey: string, width: number) => void;
  onReset?: (columnKey: string) => void;
}

/**
 * Drag handle posicionado en el borde derecho del <th>.
 *
 * Estrategia de medición:
 * - Durante el drag NO se hace setState ni re-render: el handle escribe
 *   `width` directamente al <th> y a todas las <td> de la misma columna
 *   vía `data-column-key`. Esto evita que el handle se desplace junto con
 *   la columna y descuadre la medición del delta.
 * - El cursor del usuario marca el "fin" del ancho de la columna: cuando
 *   el cursor se mueve 10px a la derecha, la columna crece 10px. Intuitivo
 *   y sin desfase.
 * - Al pointerup se notifica `onCommit` una sola vez para persistir.
 *
 * Zona hot:
 * - 8px (w-2) en el borde derecho del <th>.
 * - Indicador visual de 2px (w-0.5) aparece en hover/active.
 *
 * Sort:
 * - Marca `data-resizing` en el <th> desde pointerdown. Se limpia en el
 *   siguiente frame tras pointerup para que el click tardío del navegador
 *   lo vea y aborte el sort.
 */
export function ColumnResizeHandle({
  columnKey,
  currentWidth,
  defaultWidth,
  minWidth,
  onCommit,
  onReset,
}: ColumnResizeHandleProps) {
  const min = minWidth ?? COLUMN_RESIZE_MIN_WIDTH;
  const startWidth = currentWidth ?? defaultWidth ?? 120;

  const dragState = useRef<{
    startX: number;
    startWidth: number;
    th: HTMLTableCellElement | null;
    pendingWidth: number | null;
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.closest("th") as HTMLTableCellElement | null;

    // Baseline del delta = ancho ACTUAL renderizado del <th>, NO el
    // persistido/default. En columnas auto-dimensionadas (sin width explícito,
    // table-layout: auto) el ancho real puede diferir mucho de
    // `currentWidth ?? defaultWidth ?? 120`. Si usáramos ese fallback, al agarrar
    // el handle la columna saltaría a ese ancho y habría que arrastrar de más
    // para recuperar el ancho visible. Medir el th evita el salto: el delta se
    // mide desde donde la columna está. (border-box ⇒ getBoundingClientRect
    // coincide con el `width` que luego aplicamos.)
    const measuredWidth = th
      ? Math.round(th.getBoundingClientRect().width)
      : startWidth;

    dragState.current = {
      startX: e.clientX,
      startWidth: Math.max(min, measuredWidth),
      th,
      pendingWidth: null,
    };

    th?.setAttribute("data-resizing", "true");

    const target = e.currentTarget;
    try {
      target.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    const applyWidth = (width: number) => {
      const cssWidth = `${width}px`;
      // Header
      th?.style.setProperty("width", cssWidth, "important");
      th?.style.setProperty("min-width", cssWidth, "important");
      // Body: todas las <td> con el mismo data-column-key.
      // Marcamos th con data-column-key para que las celdas puedan
      // identificarse sin un mapping externo.
      const table = th?.closest("table");
      if (table && th) {
        const key = th.getAttribute("data-column-key");
        if (key) {
          const tds = table.querySelectorAll<HTMLTableCellElement>(
            `td[data-column-key="${CSS.escape(key)}"]`,
          );
          tds.forEach((td) => {
            td.style.setProperty("width", cssWidth, "important");
            td.style.setProperty("min-width", cssWidth, "important");
            // El texto truncado acompaña el ancho en vivo durante el drag.
            td.style.setProperty("--col-w", cssWidth);
            td.setAttribute("data-col-sized", "");
          });
        }
      }
    };

    let frameRequested = false;

    const handlePointerMove = (ev: PointerEvent) => {
      if (!dragState.current) return;
      const delta = ev.clientX - dragState.current.startX;
      const next = Math.max(min, dragState.current.startWidth + delta);
      dragState.current.pendingWidth = next;

      // Throttle via rAF: evita aplicar el width más de una vez por frame,
      // mejora el feedback sin re-render de React.
      if (!frameRequested) {
        frameRequested = true;
        requestAnimationFrame(() => {
          frameRequested = false;
          if (dragState.current?.pendingWidth != null) {
            applyWidth(dragState.current.pendingWidth);
          }
        });
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      const finalWidth =
        dragState.current?.pendingWidth ?? dragState.current?.startWidth ?? null;
      const cell = dragState.current?.th ?? null;
      if (cell) {
        requestAnimationFrame(() => cell.removeAttribute("data-resizing"));
      }
      dragState.current = null;
      if (finalWidth != null) {
        onCommit(columnKey, finalWidth);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const onDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onReset) onReset(columnKey);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Cambiar ancho de la columna ${columnKey}. Doble click para resetear.`}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      className="group absolute right-0 top-0 z-10 flex h-full w-2 cursor-col-resize items-center justify-center touch-none select-none"
    >
      <div className="h-full w-0.5 bg-transparent group-hover:bg-primary/40 group-active:bg-primary/60" />
    </div>
  );
}
